import { useEffect, useState } from "react";
import { Instagram, ArrowUpRight, Play } from "lucide-react";
import { Button } from "./ui/button";
import Reveal from "./Reveal";
import SectionLabel from "./SectionLabel";
import { useSite, resolveImg } from "../context/SiteContext";
import { api } from "../lib/api";

// Fetches live Instagram feed from backend Graph API cache.
// Falls back to the CMS-managed static grid if no live feed is configured.
export default function InstagramSection() {
  const { brand, instagram } = useSite();
  const [live, setLive] = useState(null); // null = loading, [] = none

  useEffect(() => {
    api
      .get("/public/instagram/feed", { params: { limit: 6 } })
      .then(({ data }) => setLive(data?.items || []))
      .catch(() => setLive([]));
  }, []);

  const handle = brand.instagramHandle;
  const usingLive = Array.isArray(live) && live.length > 0;
  const tiles = usingLive
    ? live.map((p) => ({
        id: p.id,
        image: p.thumbnail_url || p.media_url,
        caption: p.caption || `View on Instagram`,
        href: p.permalink,
        isVideo: p.media_type === "VIDEO",
      }))
    : (instagram || []).slice(0, 6).map((p) => ({
        id: p.id,
        image: resolveImg(p.image),
        caption: p.caption,
        href: brand.instagramUrl,
        isVideo: false,
      }));

  return (
    <section data-testid="instagram-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>Instagram</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-light max-w-3xl text-balance">
              Follow along on <em className="not-italic font-medium">@{handle}</em>
            </h2>
            <a href={brand.instagramUrl} target="_blank" rel="noreferrer" data-testid="instagram-follow">
              <Button variant="outline" className="rounded-full h-11 px-5">
                <Instagram className="h-4 w-4 mr-2" /> Follow us
              </Button>
            </a>
          </div>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
          {tiles.map((p, i) => (
            <Reveal key={p.id} delay={(i % 6) * 0.04}>
              <a
                href={p.href}
                target="_blank"
                rel="noreferrer"
                className="group relative aspect-square block overflow-hidden rounded-2xl"
                data-testid={`ig-tile-${i}`}
                aria-label={p.caption}
              >
                <img
                  src={p.image}
                  alt={p.caption}
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  loading="lazy"
                />
                {p.isVideo && (
                  <span className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-black/50 text-white backdrop-blur-sm">
                    <Play className="h-3.5 w-3.5 fill-white" />
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  <div className="flex items-center justify-between w-full text-white text-xs">
                    <span className="truncate">{p.caption}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
